// Analytics gate — decides whether PostHog is allowed to initialize at all.
//
// Three suppression rules, any one of which blocks init:
//   1. localhost / 127.0.0.1 / ::1 / *.local  — dev-server traffic
//   2. non-production builds (NODE_ENV !== "production")
//   3. the internal-user flag, for Ben's own prod devices
//
// Internal flag: visiting any page with ?internal=1 writes a persistent
// localStorage flag; ?internal=0 clears it. The flag is read on every load, so
// a flagged device stays silent on prod without needing the query string again.
//
// This module is init/wrapper level ONLY. It never touches event names or
// capture call sites (mockAnalytics.js / recapAnalytics.js are untouched).

const INTERNAL_KEY = "auctionArchitect_internalUser";

export function isLocalHost() {
  if (typeof window === "undefined") return false;
  const h = window.location.hostname || "";
  return h === "localhost" || h === "127.0.0.1" || h === "::1" || h === "" || h.endsWith(".local");
}

export function isDevBuild() {
  return process.env.NODE_ENV !== "production";
}

// Only the canonical prod domain may capture. Vercel preview deploys
// (*.vercel.app) are production-mode builds on a non-localhost host, so without
// this they'd pass isLocalHost()/isDevBuild() and pollute prod PostHog. Any
// non-canonical host (previews, custom aliases) is auto-suppressed.
// NOTE: update isCanonicalHost() if the prod domain ever changes.
export function isCanonicalHost() {
  if (typeof window === "undefined") return false;
  const h = window.location.hostname || "";
  return h === "mock1.app" || h === "www.mock1.app";
}

// Reads ?internal=1 / ?internal=0 and persists the result. Called before the
// allow-check so one visit both flags the device and suppresses that session.
export function syncInternalFlagFromUrl() {
  if (typeof window === "undefined") return;
  try {
    const v = new URLSearchParams(window.location.search).get("internal");
    if (v === "1") localStorage.setItem(INTERNAL_KEY, "1");
    else if (v === "0") localStorage.removeItem(INTERNAL_KEY);
  } catch { /* private mode / storage disabled — fall through to unflagged */ }
}

export function isInternalUser() {
  try { return localStorage.getItem(INTERNAL_KEY) === "1"; } catch { return false; }
}

export function analyticsAllowed() {
  return isCanonicalHost() && !isLocalHost() && !isDevBuild() && !isInternalUser();
}

// Transmitting methods stubbed when analytics is suppressed. Without this, an
// uninitialized posthog singleton still receives calls from existing call
// sites; stubbing guarantees they are silent no-ops (no network, no console
// noise) without editing a single call site.
const TRANSMIT_METHODS = ["capture", "identify", "alias", "group", "setPersonProperties", "startSessionRecording"];

function silence(posthog) {
  TRANSMIT_METHODS.forEach(m => {
    if (typeof posthog[m] === "function") posthog[m] = () => undefined;
  });
}

// Returns true if PostHog actually initialized, false if suppressed.
export function initAnalytics(posthog, apiKey, options) {
  syncInternalFlagFromUrl();
  if (!analyticsAllowed()) {
    silence(posthog);
    return false;
  }
  posthog.init(apiKey, options);
  return true;
}
